import { useEffect, useState } from 'react';

/**
 * Live elapsed-time display for the active workout screen. Master Phase 11.
 *
 * Same shape as `useRestTimer.ts`: derives everything from a durable instant
 * (`startedAt`, already persisted on every `SessionRecord`) against the
 * current time, rather than an in-memory countdown that would reset on
 * reload or a backgrounded tab. `requestAnimationFrame` only runs while the
 * document is visible (Phase 0 §18's battery rule) — identical tradeoff to
 * the rest timer, so a backgrounded tab simply catches up on `now` the
 * moment it's foregrounded again rather than ticking unseen.
 *
 * There is no separate "time spent paused" field in `SessionRecord` — the
 * existing post-workout report (`WorkoutSummary.tsx`) already computes
 * Duration as `(endedAt ?? Date.now()) - startedAt`, i.e. wall-clock time
 * including any pauses. This live timer intentionally matches that exact
 * calculation so the number on screen during the workout is the same number
 * the final report shows, not a second, diverging notion of "duration".
 */
export function useElapsedTimer(startedAt: number, endedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (endedAt !== null) return;

    let frame: number | null = null;

    function scheduleIfVisible(): void {
      if (document.visibilityState !== 'visible') return;
      frame = requestAnimationFrame(() => {
        setNow(Date.now());
        scheduleIfVisible();
      });
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        if (frame === null) scheduleIfVisible();
      } else if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    }

    setNow(Date.now());
    scheduleIfVisible();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [startedAt, endedAt]);

  const end = endedAt ?? now;
  return Math.max(0, Math.floor((end - startedAt) / 1000));
}
